import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { cajaService } from '@/services/caja.service';
import { Loader2, Plus, Minus, DollarSign, Clock, Check, X, Wallet, CheckCircle2 } from 'lucide-react';
import { useApiError } from '@/hooks/useApiError';
import { ApiErrorDisplay } from '@/components/ApiErrorDisplay';
import { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { ZReportTicket } from '@/components/ZReportTicket';

interface CashRegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CashRegisterModal({ isOpen, onClose }: CashRegisterModalProps) {
  const { user, logout } = useAuthStore();
  const [register, setRegister] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showOpenForm, setShowOpenForm] = useState(false);
  const [showCloseForm, setShowCloseForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [initialCash, setInitialCash] = useState('500');
  const [openNote, setOpenNote] = useState('');
  const [countedCash, setCountedCash] = useState('');
  const [closeNote, setCloseNote] = useState('');
  const [openAmountError, setOpenAmountError] = useState('');
  const { error, errorType, handleApiError, handleAuthRedirect } = useApiError();
  const printRef = useRef<HTMLDivElement>(null);
  
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    onAfterPrint: () => {
      setShowCloseForm(false);
      setCountedCash('');
      setCloseNote('');
      loadStatus();
      
      // Force logout after closing register (Corte Z) to enforce daily login / new session
      logout();
      window.location.href = '/login';
    }
  });

  const isAdmin = ['SUPER_ADMIN', 'ADMIN', 'GERENTE'].includes(user?.role || '');

  const loadStatus = useCallback(async () => {
    if (!isOpen || !user?.branch?.id) return;
    setLoading(true);
    try {
      const result = await cajaService.getStatus(user.branch.id);
      setRegister(result);
      const hist = await cajaService.history(user.branch.id, 1, 10);
      setHistory(hist.data || []);
    } catch (err: any) { handleApiError(err); }
    setLoading(false);
  }, [user]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Non-admins go directly to open/close forms
  useEffect(() => {
    if (isOpen && !loading && !isAdmin && register) {
      if (register.status === 'CLOSED' && !showOpenForm && !showCloseForm) {
        setShowOpenForm(true);
      } else if (register.status === 'OPEN' && !showOpenForm && !showCloseForm) {
        setShowCloseForm(true);
      }
    }
    
    // Reset state when closed
    if (!isOpen) {
      setShowOpenForm(false);
      setShowCloseForm(false);
    }
  }, [isOpen, loading, isAdmin, register, showOpenForm, showCloseForm]);

  const handleOpen = async () => {
    if (!user?.branch?.id || !user?.id) return;
    const amount = parseFloat(initialCash);
    if (isNaN(amount) || amount < 0) {
      setOpenAmountError('Ingresa un monto válido');
      return;
    }
    setSaving(true);
    try {
      await cajaService.open({
        branchId: user.branch.id,
        openedById: user.id,
        initialCash: amount,
        note: openNote || undefined,
      });
      setShowOpenForm(false);
      setInitialCash('500');
      setOpenNote('');
      loadStatus();
      if (!isAdmin) {
        onClose();
      }
    } catch (err: any) { handleApiError(err); }
    setSaving(false);
  };

  const handleClose = async () => {
    if (!register?.id || !user?.id) return;
    const amount = parseFloat(countedCash);
    if (isNaN(amount) || amount < 0) return;
    setSaving(true);
    try {
      const closed = await cajaService.close(register.id, {
        closedById: user.id,
        countedCash: amount,
        note: closeNote || undefined,
      });
      
      // Fetch full register data to ensure sales and payments are included for the ticket breakdown
      const fullClosed = await cajaService.findById(closed.id);
      
      // Update register with closed data so ticket gets all info
      setRegister(fullClosed);
      
      // Print the ticket
      setTimeout(() => {
        handlePrint();
        setSaving(false);
      }, 300);
      
    } catch (err: any) { 
      handleApiError(err); 
      setSaving(false);
    }
  };

  const formatCurrency = (val: any) => `$${Number(val || 0).toFixed(2)}`;
  const formatDate = (d: string) => new Date(d).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 size={24} className="animate-spin text-ios-gray" />
      </div>
    );
  }

  if (!isOpen) return null;

  return (
    <>
      {/* Dashboard - Only for Admin */}
      {isAdmin && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 sm:p-6 lg:p-8">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl mx-auto max-h-[90vh] flex flex-col overflow-hidden relative">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h1 className="text-2xl font-bold">Caja</h1>
          <div className="flex items-center gap-4">
            {register?.status === 'OPEN' ? (
              <button onClick={() => { setCountedCash(String(register.expectedCash || 0)); setShowCloseForm(true); }} className="px-4 py-2 rounded-xl text-sm bg-red-500 text-white hover:bg-red-600 font-bold flex items-center gap-2 transition-colors">
                <Minus size={16} />
                Cerrar Caja
              </button>
            ) : (
              <button onClick={() => setShowOpenForm(true)} className="px-4 py-2 rounded-xl text-sm bg-blue-500 text-white hover:bg-blue-600 font-bold flex items-center gap-2 transition-colors">
                <Plus size={16} />
                Abrir Caja
              </button>
            )}
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors">
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <ApiErrorDisplay error={error} errorType={errorType} onRetry={loadStatus} onLogin={handleAuthRedirect} />
          {!error && (
          <>
      {/* Status Card */}
      <div className="ios-card p-6">
        {register?.status === 'OPEN' ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-ios-green rounded-full animate-pulse" />
              <span className="font-semibold text-ios-green">Caja Abierta</span>
              <span className="text-xs text-ios-gray">Desde {formatDate(register.openedAt)}</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-ios-gray-6 rounded-[12px] p-3">
                <p className="text-[10px] text-ios-gray uppercase">Fondo inicial</p>
                <p className="text-lg font-bold mt-1">{formatCurrency(register.initialCash)}</p>
              </div>
              {isAdmin && (
                <>
                  <div className="bg-ios-gray-6 rounded-[12px] p-3">
                    <p className="text-[10px] text-ios-gray uppercase">Ventas</p>
                    <p className="text-lg font-bold mt-1 text-ios-blue">{formatCurrency(register.totalSales)}</p>
                  </div>
                  <div className="bg-ios-gray-6 rounded-[12px] p-3">
                    <p className="text-[10px] text-ios-gray uppercase">Esperado</p>
                    <p className="text-lg font-bold mt-1">{formatCurrency(register.expectedCash)}</p>
                  </div>
                </>
              )}
              <div className="bg-ios-gray-6 rounded-[12px] p-3">
                <p className="text-[10px] text-ios-gray uppercase">Ventas hoy</p>
                <p className="text-lg font-bold mt-1">{register.saleCount || 0}</p>
              </div>
            </div>

            <div className="text-xs text-ios-gray">
              Abierta por: <span className="font-medium text-foreground">{register.openedBy?.fullName}</span>
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            <div className="w-12 h-12 bg-ios-gray-6 rounded-full flex items-center justify-center mx-auto mb-3">
              <DollarSign size={24} className="text-ios-gray" />
            </div>
            <p className="font-medium">No hay caja abierta</p>
            <p className="text-sm text-ios-gray mt-1">Abre la caja para comenzar a registrar ventas</p>
          </div>
        )}
      </div>

      {/* History */}
      {history.length > 0 && (
        <div>
          <h2 className="font-semibold mb-3">Cortes anteriores</h2>
          <div className="space-y-2">
            {history.map((h: any) => (
              <div key={h.id} className="ios-card p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${h.status === 'CLOSED' ? 'bg-ios-green/10' : 'bg-yellow-50'}`}>
                    {h.status === 'CLOSED' ? <Check size={16} className="text-ios-green" /> : <Clock size={16} className="text-yellow-500" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{formatDate(h.openedAt)}</p>
                    <p className="text-xs text-ios-gray">
                      {h.openedBy?.fullName} · {h._count?.sales || 0} ventas
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  {isAdmin && (
                    <>
                      <p className="text-sm font-semibold">{formatCurrency(h.totalSales)}</p>
                      {h.difference !== null && (
                        <p className={`text-xs ${Number(h.difference) >= 0 ? 'text-ios-green' : 'text-ios-red'}`}>
                          Dif: {formatCurrency(h.difference)}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
          </>
          )}
        </div>
      </div>
    </div>
    )}

      {/* Open Register Modal */}
      {showOpenForm && (
        <div className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 ${isAdmin ? 'z-[110]' : 'z-[100]'}`}>
          <div className="bg-white rounded-[24px] w-full max-w-sm shadow-2xl overflow-hidden">
            <div className="p-4 border-b border-ios-gray-5">
              <h3 className="font-semibold">Abrir Caja</h3>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs text-ios-gray mb-1 block">Monto inicial *</label>
                <input
                  type="number"
                  value={initialCash}
                  onChange={(e) => { setInitialCash(e.target.value); setOpenAmountError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleOpen(); }}
                  placeholder="0.00"
                  className="ios-input w-full text-lg font-semibold"
                  step="0.01"
                  autoFocus
                />
                {openAmountError && <p className="text-xs text-ios-red mt-1">{openAmountError}</p>}
              </div>
              <div>
                <label className="text-xs text-ios-gray mb-1 block">Nota (opcional)</label>
                <input
                  type="text"
                  value={openNote}
                  onChange={(e) => setOpenNote(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleOpen(); }}
                  placeholder="Notas de apertura..."
                  className="ios-input w-full text-sm"
                />
              </div>
            </div>
            <div className="p-4 bg-gray-50/50 flex gap-2">
              <button 
                onClick={() => {
                  setShowOpenForm(false);
                  if (!isAdmin) onClose();
                }} 
                className="flex-1 px-4 py-3 rounded-xl text-sm font-bold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleOpen}
                disabled={saving || !initialCash}
                className="flex-1 px-4 py-3 rounded-xl text-sm font-bold text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-50"
              >
                {saving ? 'Abriendo...' : 'Abrir Caja'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close Register Modal (Redesigned) */}
      {showCloseForm && (
        <div className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 ${isAdmin ? 'z-[110]' : 'z-[100]'}`}>
          <div className="bg-white rounded-[24px] w-full max-w-md shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-[#cc2229] p-6 relative">
              <button 
                onClick={() => {
                  setShowCloseForm(false);
                  if (!isAdmin) onClose();
                }} 
                className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
              <h3 className="text-2xl font-bold text-white">Cerrar Turno</h3>
              <p className="text-white/80 text-sm mt-1">Finaliza el turno actual</p>
            </div>
            
            <div className="p-6 pb-2 space-y-6">
              {/* Alert message */}
              <div className="bg-[#fff1f2] border border-[#fecdd3] text-[#e11d48] rounded-2xl p-4 flex gap-3">
                <Wallet className="shrink-0 mt-0.5" size={20} />
                <p className="text-sm">
                  Ingresa el <strong>monto total en efectivo</strong> que tienes físicamente ahora mismo.
                </p>
              </div>

              {/* Input for counted cash */}
              <div className="space-y-2">
                <label className="block text-center text-sm font-bold text-gray-400">
                  Efectivo Contado ($)
                </label>
                <input
                  type="number"
                  value={countedCash}
                  onChange={(e) => setCountedCash(e.target.value)}
                  placeholder="0.00"
                  className="w-full text-center text-5xl font-black text-[#5c6a82] bg-gray-50 rounded-2xl py-6 focus:outline-none focus:ring-2 focus:ring-[#e11d48]/20 transition-all"
                  step="0.01"
                  autoFocus
                />
              </div>

              {/* Input for notes */}
              <div className="space-y-2">
                <label className="block text-sm font-bold text-gray-400">
                  Observaciones (Opcional)
                </label>
                <textarea
                  value={closeNote}
                  onChange={(e) => setCloseNote(e.target.value)}
                  placeholder="Notas sobre el turno..."
                  className="w-full bg-gray-50 text-gray-500 rounded-2xl p-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#e11d48]/20 resize-none h-24 transition-all"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 flex gap-3">
              <button 
                onClick={() => {
                  setShowCloseForm(false);
                  if (!isAdmin) onClose();
                }} 
                className="px-6 py-4 rounded-2xl text-sm font-bold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleClose}
                disabled={saving || !countedCash}
                className="flex-1 px-4 py-4 rounded-2xl text-lg font-bold text-white bg-[#da292e] hover:bg-[#b91c1c] disabled:opacity-50 flex items-center justify-center gap-2 transition-colors shadow-lg shadow-red-500/30"
              >
                {saving ? 'Cerrando...' : (
                  <>
                    <CheckCircle2 size={24} />
                    Confirmar y Cerrar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Hidden ticket for printing */}
      <ZReportTicket register={register} ref={printRef} />
    </>
  );
}
